import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { APP_ERROR_CODES, isAppErrorData } from "@repo/shared";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import type { AssistantAskUserAnswerMapRuntime, AssistantAskUserPrompt } from "../types";

export const ASK_USER_DRAFT_DEBOUNCE_MS = 600;
// Mirrors CLAUDIA_INPUT_LIMITS.askUserCustomAnswerChars in packages/convex/convex/assistant/inputLimits.ts.
export const ASK_USER_CUSTOM_ANSWER_MAX = 4_000;
/** Share of the limit at which the counter appears; the composer warns at the same point. */
export const ASK_USER_CUSTOM_ANSWER_WARNING_RATIO = 0.9;

export interface CustomAnswerDraft {
  markdown: string;
  plainText: string;
}

type AskUserDraftStatus = "editing" | "resolving" | "closed";

interface AskUserDraftState {
  activeRequestId: string;
  questionIndex: number;
  answers: AssistantAskUserAnswerMapRuntime;
  customDrafts: Record<string, CustomAnswerDraft>;
  activeCustomQuestionId: string | null;
  composerSeed: { questionId: string; markdown: string } | null;
  intentVersions: Record<string, number>;
  status: AskUserDraftStatus;
}

type AskUserDraftAction =
  | { type: "RESET"; request: AssistantAskUserPrompt }
  | { type: "GO_TO"; index: number; seed: { questionId: string; markdown: string } | null }
  | { type: "SELECT_OPTION"; questionId: string; optionId: string; version: number }
  | { type: "ACTIVATE_CUSTOM"; questionId: string; seedMarkdown: string }
  | {
      type: "SET_CUSTOM_DRAFT";
      questionId: string;
      draft: CustomAnswerDraft;
      version: number | null;
    }
  | { type: "SYNC_FROM_DRAFT"; draftAnswers: AssistantAskUserAnswerMapRuntime }
  | { type: "SET_STATUS"; status: AskUserDraftStatus };

export function getNextQuestionIndex(
  questions: AssistantAskUserPrompt["questions"],
  answers: AssistantAskUserAnswerMapRuntime,
  currentIndex: number
) {
  const laterIndex = questions.findIndex(
    (question, index) => index > currentIndex && !answers[question.id]
  );
  if (laterIndex >= 0) {
    return laterIndex;
  }
  const earlierIndex = questions.findIndex(
    (question, index) => index < currentIndex && !answers[question.id]
  );
  return earlierIndex >= 0 ? earlierIndex : currentIndex;
}

export function hasAllAskUserAnswers(
  questions: AssistantAskUserPrompt["questions"],
  answers: AssistantAskUserAnswerMapRuntime
) {
  return questions.every((question) => Boolean(answers[question.id]));
}

export function getCustomAnswerDrafts(
  answers: AssistantAskUserAnswerMapRuntime | null | undefined
) {
  const next: Record<string, CustomAnswerDraft> = {};
  for (const answer of Object.values(answers ?? {})) {
    if (answer.kind === "custom") {
      next[answer.questionId] = {
        markdown: answer.text,
        plainText: answer.text,
      };
    }
  }
  return next;
}

export function getInitialCustomComposerSeed(
  request: AssistantAskUserPrompt,
  drafts: Record<string, CustomAnswerDraft>
) {
  const initialQuestion =
    request.questions.find((question) => request.draftAnswers?.[question.id]?.kind === "custom") ??
    request.questions[0];
  if (!initialQuestion || request.draftAnswers?.[initialQuestion.id]?.kind !== "custom") {
    return null;
  }

  return {
    questionId: initialQuestion.id,
    markdown: drafts[initialQuestion.id]?.markdown ?? "",
  };
}

function initState(request: AssistantAskUserPrompt): AskUserDraftState {
  const customDrafts = getCustomAnswerDrafts(request.draftAnswers);
  return {
    activeRequestId: request.requestId,
    questionIndex: 0,
    answers: request.draftAnswers ?? {},
    customDrafts,
    activeCustomQuestionId: null,
    composerSeed: getInitialCustomComposerSeed(request, customDrafts),
    intentVersions: {},
    status: "editing",
  };
}

function askUserDraftReducer(
  state: AskUserDraftState,
  action: AskUserDraftAction
): AskUserDraftState {
  switch (action.type) {
    case "RESET":
      return initState(action.request);
    case "GO_TO":
      return { ...state, questionIndex: action.index, composerSeed: action.seed };
    case "SELECT_OPTION": {
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: {
            questionId: action.questionId,
            kind: "option",
            optionId: action.optionId,
          },
        },
        activeCustomQuestionId:
          state.activeCustomQuestionId === action.questionId ? null : state.activeCustomQuestionId,
        intentVersions: { ...state.intentVersions, [action.questionId]: action.version },
      };
    }
    case "ACTIVATE_CUSTOM": {
      const answers = { ...state.answers };
      return {
        ...state,
        answers,
        activeCustomQuestionId: action.questionId,
        composerSeed: { questionId: action.questionId, markdown: action.seedMarkdown },
      };
    }
    case "SET_CUSTOM_DRAFT": {
      const answers = { ...state.answers };
      const text = getAnswerText(action.draft);
      if (text) {
        answers[action.questionId] = {
          questionId: action.questionId,
          kind: "custom",
          text,
        };
      }
      return {
        ...state,
        answers,
        customDrafts: { ...state.customDrafts, [action.questionId]: action.draft },
        activeCustomQuestionId: action.questionId,
        intentVersions:
          action.version === null
            ? state.intentVersions
            : { ...state.intentVersions, [action.questionId]: action.version },
      };
    }
    case "SYNC_FROM_DRAFT":
      return { ...state, answers: action.draftAnswers };
    case "SET_STATUS":
      return { ...state, status: action.status };
    default:
      return state;
  }
}

function getAnswerText(draft: CustomAnswerDraft | undefined) {
  const plainText = draft?.plainText.trim() ?? "";
  if (!plainText) {
    return "";
  }
  return draft?.markdown.trim() || plainText;
}

function getQuestionSeed(state: AskUserDraftState, question: { id: string } | undefined) {
  if (!question) {
    return null;
  }
  const answer = state.answers[question.id];
  if (answer?.kind !== "custom" && state.activeCustomQuestionId !== question.id) {
    return null;
  }
  const draft = state.customDrafts[question.id];
  return {
    questionId: question.id,
    markdown: draft?.markdown ?? (answer?.kind === "custom" ? answer.text : ""),
  };
}

function getNextVersion(state: AskUserDraftState, questionId: string) {
  return (state.intentVersions[questionId] ?? 0) + 1;
}

function getAppErrorData(cause: unknown) {
  const data = (cause as { data?: unknown })?.data;
  if (isAppErrorData(data)) {
    return data;
  }
  return null;
}

export function isAskUserNotPendingError(cause: unknown) {
  const data = getAppErrorData(cause);
  return data?.code === APP_ERROR_CODES.CONFLICT && data.details?.reason === "ask_user_not_pending";
}

export function useAskUserDraft(params: {
  projectId: Id<"projects">;
  /**
   * The conversation this question was read from. Sent with the answer so the backend can refuse
   * a request id held across a thread switch — answering resumes that conversation's run
   * (LAT-990).
   */
  threadId: string | null;
  request: AssistantAskUserPrompt;
  reportError: (cause: unknown, fallback: string) => void;
  isCurrentQuestionVoiceBusy?: boolean;
  /** Run to completion before the answer is submitted; see `finalizeFromSnapshot`. */
}) {
  const { projectId, threadId, request, reportError, isCurrentQuestionVoiceBusy = false } = params;
  const [state, dispatch] = useReducer(askUserDraftReducer, request, initState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const lastRequestRef = useRef(request);
  const debounceTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const lastSavedText = useRef(new Map<string, string>());
  const lengthErrorReported = useRef(new Set<string>());
  const writeChains = useRef(new Map<string, Promise<unknown>>());
  const closedReportedRef = useRef(false);

  const saveDraftAnswer = useObservedMutation(api.claudia.saveAskUserDraftAnswer, {
    feature: "assistant",
    operation: "save-ask-user-draft-answer",
    includeRequestId: true,
  });
  const answerAskUser = useObservedMutation(api.claudia.answerAskUser, {
    feature: "assistant",
    operation: "answer-ask-user",
    includeRequestId: true,
  });
  const dismissAskUser = useObservedMutation(api.claudia.dismissAskUser, {
    feature: "assistant",
    operation: "dismiss-ask-user",
    includeRequestId: true,
  });

  const applyAction = useCallback((action: AskUserDraftAction) => {
    stateRef.current = askUserDraftReducer(stateRef.current, action);
    dispatch(action);
  }, []);

  const cancelDebounce = useCallback((questionId: string) => {
    const timer = debounceTimers.current.get(questionId);
    if (!timer) {
      return;
    }
    clearTimeout(timer);
    debounceTimers.current.delete(questionId);
  }, []);

  const cancelAllDebounces = useCallback(() => {
    for (const timer of debounceTimers.current.values()) {
      clearTimeout(timer);
    }
    debounceTimers.current.clear();
  }, []);

  const enqueueWrite = useCallback(<T>(questionId: string, fn: () => Promise<T>): Promise<T> => {
    const previous = writeChains.current.get(questionId) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(fn);
    writeChains.current.set(questionId, next);
    void next.finally(() => {
      if (writeChains.current.get(questionId) === next) {
        writeChains.current.delete(questionId);
      }
    });
    return next;
  }, []);

  const handleWriteError = useCallback(
    (cause: unknown, fallback: string) => {
      if (isAskUserNotPendingError(cause)) {
        applyAction({ type: "SET_STATUS", status: "closed" });
        if (!closedReportedRef.current) {
          closedReportedRef.current = true;
          reportError(
            new Error("This question is no longer active."),
            "This question is no longer active."
          );
        }
        return;
      }
      reportError(cause, fallback);
    },
    [applyAction, reportError]
  );

  const persistCustomAnswer = useCallback(
    (questionId: string, draft: CustomAnswerDraft) =>
      enqueueWrite(questionId, async () => {
        if (stateRef.current.activeRequestId !== request.requestId) {
          return null;
        }
        const text = getAnswerText(draft);
        if (!text) {
          return lastRequestRef.current;
        }
        if (text.length > ASK_USER_CUSTOM_ANSWER_MAX) {
          if (!lengthErrorReported.current.has(questionId)) {
            lengthErrorReported.current.add(questionId);
            reportError(new Error("ask_user_custom_answer_too_long"), "Answer is too long.");
          }
          return null;
        }
        lengthErrorReported.current.delete(questionId);
        if (lastSavedText.current.get(questionId) === text) {
          return lastRequestRef.current;
        }
        const versionAtCall = stateRef.current.intentVersions[questionId] ?? 0;
        try {
          const updated = await saveDraftAnswer({
            projectId,
            askUserRequestId: request.requestId as Id<"assistantAskUserRequests">,
            answer: { questionId, kind: "custom", text },
          });
          lastRequestRef.current = updated;
          lastSavedText.current.set(questionId, text);
          if ((stateRef.current.intentVersions[questionId] ?? 0) === versionAtCall) {
            applyAction({ type: "SYNC_FROM_DRAFT", draftAnswers: updated.draftAnswers ?? {} });
          }
          return updated;
        } catch (cause: unknown) {
          handleWriteError(cause, "Failed to save answer");
          return null;
        }
      }),
    [
      applyAction,
      enqueueWrite,
      handleWriteError,
      projectId,
      reportError,
      request.requestId,
      saveDraftAnswer,
    ]
  );

  const persistCustomAnswerRef = useRef(persistCustomAnswer);
  persistCustomAnswerRef.current = persistCustomAnswer;

  const restoreDurableAnswer = useCallback(
    (answer: AssistantAskUserAnswerMapRuntime[string]) =>
      enqueueWrite(answer.questionId, async () => {
        if (stateRef.current.activeRequestId !== request.requestId) {
          return null;
        }
        try {
          const updated = await saveDraftAnswer({
            projectId,
            askUserRequestId: request.requestId as Id<"assistantAskUserRequests">,
            answer,
          });
          lastRequestRef.current = updated;
          if (answer.kind === "custom") {
            lastSavedText.current.set(answer.questionId, answer.text);
          } else {
            lastSavedText.current.delete(answer.questionId);
          }
          applyAction({ type: "SYNC_FROM_DRAFT", draftAnswers: updated.draftAnswers ?? {} });
          return updated;
        } catch (cause: unknown) {
          handleWriteError(cause, "Failed to update answer");
          return null;
        }
      }),
    [applyAction, enqueueWrite, handleWriteError, projectId, request.requestId, saveDraftAnswer]
  );

  const goTo = useCallback(
    (index: number) => {
      const boundedIndex = Math.min(Math.max(0, index), request.questions.length - 1);
      const question = request.questions[boundedIndex];
      applyAction({
        type: "GO_TO",
        index: boundedIndex,
        seed: getQuestionSeed(stateRef.current, question),
      });
    },
    [applyAction, request.questions]
  );

  const finalizeFromSnapshot = useCallback(
    async (snapshot: AssistantAskUserPrompt | null) => {
      const draftAnswers = snapshot?.draftAnswers ?? lastRequestRef.current.draftAnswers ?? {};
      const missingIndex = request.questions.findIndex((question) => !draftAnswers[question.id]);
      if (missingIndex >= 0) {
        goTo(missingIndex);
        return;
      }
      applyAction({ type: "SET_STATUS", status: "resolving" });
      try {
        await answerAskUser({
          projectId,
          ...(threadId ? { threadId } : {}),
          askUserRequestId: request.requestId as Id<"assistantAskUserRequests">,
        });
      } catch (cause: unknown) {
        if (isAskUserNotPendingError(cause)) {
          applyAction({ type: "SET_STATUS", status: "closed" });
          if (!closedReportedRef.current) {
            closedReportedRef.current = true;
            reportError(
              new Error("This question is no longer active."),
              "This question is no longer active."
            );
          }
          return;
        }
        reportError(cause, "Failed to answer Claudia's question");
        applyAction({ type: "SET_STATUS", status: "editing" });
      }
    },
    [
      answerAskUser,
      applyAction,
      goTo,
      projectId,
      reportError,
      request.questions,
      request.requestId,
      threadId,
    ]
  );

  const commitCurrentQuestion = useCallback(async () => {
    if (stateRef.current.status !== "editing") {
      return null;
    }
    if (isCurrentQuestionVoiceBusy) {
      reportError(
        new Error("ask_user_voice_input_in_progress"),
        "Finish or cancel voice input before continuing."
      );
      return null;
    }
    const currentState = stateRef.current;
    const question = request.questions[currentState.questionIndex];
    if (!question) {
      return lastRequestRef.current;
    }
    cancelDebounce(question.id);
    const isActivated = currentState.activeCustomQuestionId === question.id;
    const draft = currentState.customDrafts[question.id];
    const text = getAnswerText(draft);
    if (isActivated && text && draft) {
      return persistCustomAnswer(question.id, draft);
    }
    if (isActivated && !text) {
      const durableAnswer = lastRequestRef.current.draftAnswers?.[question.id];
      if (durableAnswer) {
        return restoreDurableAnswer(durableAnswer);
      }
    }
    return lastRequestRef.current;
  }, [
    cancelDebounce,
    isCurrentQuestionVoiceBusy,
    persistCustomAnswer,
    reportError,
    request.questions,
    restoreDurableAnswer,
  ]);

  const goNext = useCallback(async () => {
    if (stateRef.current.status !== "editing") {
      return;
    }
    const result = await commitCurrentQuestion();
    if (result === null) {
      return;
    }
    goTo(stateRef.current.questionIndex + 1);
  }, [commitCurrentQuestion, goTo]);

  const goPrev = useCallback(async () => {
    if (stateRef.current.status !== "editing") {
      return;
    }
    const result = await commitCurrentQuestion();
    if (result === null) {
      return;
    }
    goTo(stateRef.current.questionIndex - 1);
  }, [commitCurrentQuestion, goTo]);

  const selectOption = useCallback(
    async (questionId: string, optionId: string) => {
      if (stateRef.current.status !== "editing") {
        return;
      }
      cancelDebounce(questionId);
      const versionAtCall = getNextVersion(stateRef.current, questionId);
      applyAction({ type: "SELECT_OPTION", questionId, optionId, version: versionAtCall });
      const updated = await enqueueWrite(questionId, async () => {
        if (stateRef.current.activeRequestId !== request.requestId) {
          return null;
        }
        try {
          const nextRequest = await saveDraftAnswer({
            projectId,
            askUserRequestId: request.requestId as Id<"assistantAskUserRequests">,
            answer: { questionId, kind: "option", optionId },
          });
          lastRequestRef.current = nextRequest;
          lastSavedText.current.delete(questionId);
          if ((stateRef.current.intentVersions[questionId] ?? 0) === versionAtCall) {
            applyAction({
              type: "SYNC_FROM_DRAFT",
              draftAnswers: nextRequest.draftAnswers ?? {},
            });
          }
          return nextRequest;
        } catch (cause: unknown) {
          handleWriteError(cause, "Failed to save answer");
          return null;
        }
      });
      if (updated) {
        await finalizeFromSnapshot(updated);
      }
    },
    [
      applyAction,
      cancelDebounce,
      enqueueWrite,
      finalizeFromSnapshot,
      handleWriteError,
      projectId,
      request.requestId,
      saveDraftAnswer,
    ]
  );

  const activateCustom = useCallback(
    (questionId: string) => {
      const answer = stateRef.current.answers[questionId];
      const draft = stateRef.current.customDrafts[questionId];
      applyAction({
        type: "ACTIVATE_CUSTOM",
        questionId,
        seedMarkdown: draft?.markdown ?? (answer?.kind === "custom" ? answer.text : ""),
      });
    },
    [applyAction]
  );

  const setCustomText = useCallback(
    (questionId: string, draft: CustomAnswerDraft) => {
      const text = getAnswerText(draft);
      applyAction({
        type: "SET_CUSTOM_DRAFT",
        questionId,
        draft,
        version: getNextVersion(stateRef.current, questionId),
      });
      cancelDebounce(questionId);
      if (!text || text.length > ASK_USER_CUSTOM_ANSWER_MAX) {
        return;
      }
      lengthErrorReported.current.delete(questionId);
      const timer = setTimeout(() => {
        debounceTimers.current.delete(questionId);
        const latest = stateRef.current.customDrafts[questionId];
        if (latest) {
          void persistCustomAnswer(questionId, latest);
        }
      }, ASK_USER_DRAFT_DEBOUNCE_MS);
      debounceTimers.current.set(questionId, timer);
    },
    [applyAction, cancelDebounce, persistCustomAnswer]
  );

  const flushCustom = useCallback(
    (questionId: string) => {
      cancelDebounce(questionId);
      const draft = stateRef.current.customDrafts[questionId];
      if (!draft) {
        return Promise.resolve(lastRequestRef.current);
      }
      return persistCustomAnswer(questionId, draft);
    },
    [cancelDebounce, persistCustomAnswer]
  );

  const submitCustomAnswer = useCallback(async () => {
    if (stateRef.current.status !== "editing") {
      return false;
    }
    if (isCurrentQuestionVoiceBusy) {
      reportError(
        new Error("ask_user_voice_input_in_progress"),
        "Finish or cancel voice input before continuing."
      );
      return false;
    }
    const currentState = stateRef.current;
    const question = request.questions[currentState.questionIndex];
    if (!question) {
      return false;
    }
    cancelDebounce(question.id);
    const draft = currentState.customDrafts[question.id];
    const text = getAnswerText(draft);
    if (!text || !draft) {
      return false;
    }
    const updated = await persistCustomAnswer(question.id, draft);
    if (updated) {
      await finalizeFromSnapshot(updated);
    }
    return updated !== null;
  }, [
    cancelDebounce,
    finalizeFromSnapshot,
    isCurrentQuestionVoiceBusy,
    persistCustomAnswer,
    reportError,
    request.questions,
  ]);

  const dismiss = useCallback(async () => {
    if (stateRef.current.status !== "editing") {
      return;
    }
    cancelAllDebounces();
    applyAction({ type: "SET_STATUS", status: "resolving" });
    try {
      await dismissAskUser({
        projectId,
        ...(threadId ? { threadId } : {}),
        askUserRequestId: request.requestId as Id<"assistantAskUserRequests">,
      });
    } catch (cause: unknown) {
      if (isAskUserNotPendingError(cause)) {
        applyAction({ type: "SET_STATUS", status: "closed" });
        return;
      }
      reportError(cause, "Failed to dismiss Claudia's question");
      applyAction({ type: "SET_STATUS", status: "editing" });
    }
  }, [
    applyAction,
    cancelAllDebounces,
    dismissAskUser,
    projectId,
    reportError,
    request.requestId,
    threadId,
  ]);

  useEffect(() => {
    lastRequestRef.current = request;
    lastSavedText.current.clear();
    for (const [questionId, answer] of Object.entries(request.draftAnswers ?? {})) {
      if (answer.kind === "custom") {
        lastSavedText.current.set(questionId, answer.text);
      }
    }
  }, [request]);

  useEffect(() => {
    if (stateRef.current.activeRequestId === request.requestId) {
      return;
    }
    cancelAllDebounces();
    writeChains.current.clear();
    lengthErrorReported.current.clear();
    closedReportedRef.current = false;
    applyAction({ type: "RESET", request });
  }, [applyAction, cancelAllDebounces, request]);

  useEffect(() => {
    return () => {
      // Best effort only: blur/navigation are the reliable persistence boundaries.
      const pending = Array.from(debounceTimers.current.keys());
      cancelAllDebounces();
      for (const questionId of pending) {
        const draft = stateRef.current.customDrafts[questionId];
        const text = getAnswerText(draft);
        if (draft && text && lastSavedText.current.get(questionId) !== text) {
          void persistCustomAnswerRef.current(questionId, draft);
        }
      }
    };
  }, [cancelAllDebounces]);

  const currentQuestion = request.questions[state.questionIndex] ?? request.questions[0];
  return {
    questionIndex: state.questionIndex,
    currentQuestion,
    answers: state.answers,
    customDrafts: state.customDrafts,
    activeCustomQuestionId: state.activeCustomQuestionId,
    composerSeed: state.composerSeed,
    status: state.status,
    isResolving: state.status === "resolving",
    isClosed: state.status === "closed",
    goNext,
    goPrev,
    goTo,
    selectOption,
    activateCustom,
    setCustomText,
    submitCustomAnswer,
    flushCustom,
    dismiss,
  };
}
